import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BackToDashboard from '../components/BackToDashboard';
import { inboxApi } from '../api/client';
import type { InboxEntry } from '../api/client';

export default function LeadDetail() {
  const { notificationId } = useParams<{ notificationId: string }>();
  const { t } = useTranslation();
  const [entry, setEntry] = useState<InboxEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    if (!notificationId) {
      setLoading(false);
      setError('Missing notification');
      return;
    }
    let cancelled = false;
    inboxApi
      .list()
      .then((res) => {
        if (cancelled) return;
        const found = res.items.find((e) => e.notificationId === notificationId);
        setEntry(found ?? null);
        if (!found) setError('Notification not found');
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [notificationId]);

  const handleMarkRead = async () => {
    if (!entry || entry.readStatus === 'read') return;
    setMarking(true);
    try {
      await inboxApi.markRead(entry.notificationId);
      setEntry((e) => (e ? { ...e, readStatus: 'read' as const } : e));
      window.dispatchEvent(new Event('inbox-updated'));
    } finally {
      setMarking(false);
    }
  };

  const formatDate = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { dateStyle: 'medium' }) + ' ' + d.toLocaleTimeString(undefined, { timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <div className="dash-page">
        <BackToDashboard />
        <p style={{ color: '#6b7280' }}>{t('common.loading')}</p>
      </div>
    );
  }

  if (error || !entry) {
    return (
      <div className="dash-page">
        <BackToDashboard />
        <div className="dash-alert dash-alert--error">{error ?? 'Not found'}</div>
        <Link to="/dashboard/leads" className="dash-btn dash-btn--secondary" style={{ textDecoration: 'none' }}>← {t('dashboard.inboxTitle')}</Link>
      </div>
    );
  }

  return (
    <div className="dash-page">
      <BackToDashboard />
      <header className="dash-page-header" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <h1 className="dash-page-title">{t('dashboard.leadDetail')}</h1>
        <span className={`dash-badge ${entry.readStatus === 'new' ? 'dash-badge--info' : 'dash-badge--neutral'}`}>{entry.readStatus === 'new' ? t('dashboard.inboxNew') : t('dashboard.inboxRead')}</span>
      </header>

      <section className="dash-card">
        <div className="dash-card-body">
          <div className="dash-form-group">
            <div className="dash-stat-label">{t('dashboard.fromAd')}</div>
            <Link to={`/listings/${entry.listingId}`} style={{ fontWeight: 600, color: 'var(--color-primary)', textDecoration: 'none' }}>{entry.listingTitleEn}</Link>
          </div>
          <div className="dash-form-group">
            <div className="dash-stat-label">{t('dashboard.name')}</div>
            <div style={{ fontWeight: 600 }}>{entry.leadName}</div>
          </div>
          {'leadSource' in entry && entry.leadSource && (
            <div className="dash-form-group">
              <div className="dash-stat-label">{t('dashboard.leadSource')}</div>
              <span style={{ fontSize: '0.9rem' }}>{String(entry.leadSource).replace(/_/g, ' ')}</span>
            </div>
          )}
          <div className="dash-form-group">
            <div className="dash-stat-label">{t('dashboard.email')}</div>
            <a href={`mailto:${entry.leadEmail}`} style={{ color: 'var(--color-primary)', fontWeight: 500 }}>{entry.leadEmail}</a>
          </div>
          {entry.leadPhone && (
            <div className="dash-form-group">
              <div className="dash-stat-label">{t('dashboard.phone')}</div>
              <a href={`tel:${entry.leadPhone}`} style={{ color: 'var(--color-primary)', fontWeight: 500 }}>{entry.leadPhone}</a>
            </div>
          )}
          <div className="dash-form-group">
            <div className="dash-stat-label">{t('dashboard.message')}</div>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.9375rem', lineHeight: 1.6 }}>{entry.leadMessage}</p>
          </div>
          <div style={{ fontSize: '0.8125rem', color: '#9ca3af' }}>{formatDate(entry.createdAt)}</div>
        </div>
      </section>

      <div className="dash-actions-inline">
        {entry.readStatus === 'new' && (
          <button type="button" disabled={marking} onClick={handleMarkRead} className="dash-btn dash-btn--primary">{marking ? '…' : t('dashboard.markAsRead')}</button>
        )}
        <Link to={`/listings/${entry.listingId}`} className="dash-btn dash-btn--secondary" style={{ textDecoration: 'none' }}>{t('dashboard.viewListing')}</Link>
        <Link to="/dashboard/leads" className="dash-btn dash-btn--secondary" style={{ textDecoration: 'none' }}>← {t('dashboard.inboxTitle')}</Link>
      </div>
    </div>
  );
}
